import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useIntl } from 'react-intl';

import { Period } from '../../../contracts';
import { formatHeidi2UpgradeAmount } from '../../../data/query/format-heidi-2-upgrade-preview';
import { usePlanPriceDisplay } from '../../settings/billing/components/pricing-plans-modal/use-plan-price-display';
import type { BillingPeriod } from '../plan-period-tabs/types';

const PLACEHOLDER = '—';

export type ClinicianSeatPrices = {
  /** Per-month price on monthly billing, e.g. "A$120". */
  monthlyPerMonth: string;
  /** Per-month equivalent of the yearly price, e.g. "A$85". */
  yearlyPerMonth: string;
  /** The yearly price, e.g. "A$1,020.00". */
  yearlyPerYear: string;
  priceIds: Record<BillingPeriod, string | undefined>;
};

function useClinicianSeatPrice(seatType: BillingSeatType): {
  prices: ClinicianSeatPrices;
  isPending: boolean;
} {
  const { locale } = useIntl();
  const monthly = usePlanPriceDisplay({
    tier: SubscriptionTier.CLINICIAN_PRO,
    seatType,
    period: Period.ONE_MONTH,
  });
  const yearly = usePlanPriceDisplay({
    tier: SubscriptionTier.CLINICIAN_PRO,
    seatType,
    period: Period.ONE_YEAR,
  });
  const yearlyPlan = yearly.plan;

  return {
    prices: {
      monthlyPerMonth: monthly.monthlyPrice ?? PLACEHOLDER,
      yearlyPerMonth: yearly.annualPrice ?? PLACEHOLDER,
      yearlyPerYear: yearlyPlan
        ? formatHeidi2UpgradeAmount(yearlyPlan.unit_amount, yearlyPlan.currency, locale)
        : PLACEHOLDER,
      priceIds: { monthly: monthly.plan?.id, annual: yearlyPlan?.id },
    },
    isPending: monthly.isPending || yearly.isPending,
  };
}

/** Clinician Standard and Max seat prices from the Heidi 2 catalogue, both billing periods. */
export function useClinicianSeatPrices() {
  const standard = useClinicianSeatPrice(BillingSeatType.STANDARD);
  const max = useClinicianSeatPrice(BillingSeatType.MAX);
  return {
    standard: standard.prices,
    max: max.prices,
    isPending: standard.isPending || max.isPending,
  };
}
